import { ProjectType } from '@buildwise/pages/projects/hooks/useProjectsQuery';
import { Avatar, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Dot from 'components/common/dot/Dot';
import { theme } from 'config/theme';
import { PROJECT } from 'constants/routes';
import moment from 'moment';
import { memo } from 'react';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';
import BuildingLineIcon from 'remixicon-react/BuildingLineIcon';
import { highlightSearchkeyWord, formatNumber } from 'utility/helpers';

type PostProjectProps = {
  project: ProjectType;
  searchKeyword: string;
};

function PostProject({ project, searchKeyword }: PostProjectProps) {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();

  return (
    <ProjectContainer>
      <Link
        to={`${PROJECT?.replace(':projectId', project.id)}${
          searchParams.get('q') ? `?q=${searchParams.get('q')}` : ''
        }`}
        state={searchKeyword && { ...state }}
      >
        <Avatar
          sx={{
            bgcolor: theme.palette.custom.lightBlue,
            mr: 2,
            width: '50px',
            height: '50px',
          }}
          variant="rounded"
        >
          <BuildingLineIcon color={theme.palette.custom.black} />
        </Avatar>
      </Link>

      <Box flexGrow={1}>
        <Link
          to={`${PROJECT?.replace(':projectId', project.id)}${
            searchParams.get('q') ? `?q=${searchParams.get('q')}` : ''
          }`}
          state={searchKeyword && { ...state }}
        >
          <ProjectName
            dangerouslySetInnerHTML={{
              __html: highlightSearchkeyWord(project?.name, searchKeyword),
            }}
          />
        </Link>
        {(project.city || project.state_code || project.country_code) && (
          <ProjectInfo>
            {project.city}
            {(project.state_code || project.country_code) && project.city ? ',' : ''}{' '}
            {project.state_code}
            {project.country_code && project.state_code ? ',' : ''} {project.country_code}
          </ProjectInfo>
        )}
        {project.totalValue && (
          <ProjectInfo>
            <b>Total Value:&nbsp;$</b> {formatNumber(project.totalValue)}
          </ProjectInfo>
        )}
        <Box display="flex">
          <>
            {project.start_date && (
              <ProjectInfo>
                <b>Start Date:&nbsp;</b> {moment(project.start_date).format('MMM DD, YYYY')}
              </ProjectInfo>
            )}
            {project.start_date && project.completion_date && <Dot />}
            {project.completion_date && (
              <ProjectInfo>
                <b>Completion Date:&nbsp;</b>{' '}
                {moment(project.completion_date).format('MMM DD, YYYY')}
              </ProjectInfo>
            )}
          </>
        </Box>
      </Box>
    </ProjectContainer>
  );
}

const ProjectContainer = styled(Box, { label: 'ProjectContainer' })({
  width: '100%',
  paddingTop: 8,
  display: 'flex',
});

const Link = styled(RouterLink, { label: 'Link' })(({ theme }) => ({
  display: 'inline-block',
  height: 'fit-content',
  width: 'fit-content',
  '&:hover': {
    textDecoration: 'underline',
    color: theme.palette.custom.link,
  },
}));

const ProjectName = styled(Typography, { label: 'ProjectName' })(({ theme }) => ({
  fontWeight: 700,
  fontSize: 16,
  lineHeight: '19.2px',
  marginBottom: 2,
  cursor: 'pointer',
  width: 'fit-content',
  display: 'block',
  '& .highlight': {
    backgroundColor: 'yellow !important',
  },
  '&:hover': {
    textDecoration: 'underline',
    color: theme.palette.custom.link,
  },
}));

const ProjectInfo = styled(Typography, { label: 'ProjectInfo' })(({ theme }) => ({
  fontWeight: 400,
  fontSize: 13,
  lineHeight: '18px',
  letterSpacing: '0.25px',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 450,
  color: theme.palette.custom.mistBlue,
}));

export default memo(PostProject);
